import React, { useRef, useState } from 'react'
import { Backdrop, Button, buttonClasses, Menu, MenuItem, paperClasses } from '@mui/material'

import { Icon } from './icon.tsx'

export interface ButtonItem<T> {
  label: string
  value: T
}
export interface ButtonSelectProps<T> {
  id: string
  items: Array<ButtonItem<T>>
  value: T
  onClick(item: ButtonItem<T>): void
}

export const ButtonSelect = <T,>({ items, id, value, onClick }: ButtonSelectProps<T>): React.ReactElement => {
  const buttonAnchor = useRef(null)
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<ButtonItem<T> | undefined>(items.find((item) => item.value === value))

  const toggleOpen = (isOpen: boolean) => () => {
    setOpen(isOpen)
  }

  const handleSelect = (item: ButtonItem<T>) => () => {
    setOpen(false)
    setSelected(item)
    onClick(item)
  }

  return (
    <>
      <Button
        aria-describedby={id}
        variant="contained"
        fullWidth
        size="medium"
        color="secondary"
        ref={buttonAnchor}
        sx={({ transitions }) => ({
          [`& .${buttonClasses.endIcon} svg`]: {
            fill: 'currentColor',
            transition: transitions.create('transform'),
            transform: `rotate(${open ? '180deg' : '0deg'})`,
          },
        })}
        endIcon={<Icon name="ArrowDown" />}
        onClick={toggleOpen(true)}
      >
        {selected?.label}
      </Button>
      <Backdrop open={open} onClick={toggleOpen(false)} sx={({ zIndex }) => ({ zIndex: zIndex.appBar })} />
      <Menu
        id="select-menu"
        anchorEl={buttonAnchor.current}
        open={open}
        onClose={toggleOpen(false)}
        sx={{
          [`& .${paperClasses.root}`]: { p: 0 },
        }}
      >
        {items.map((selectItem) => (
          <MenuItem
            key={selectItem.value as string}
            selected={selectItem.value === selected?.value}
            onClick={handleSelect(selectItem)}
          >
            {selectItem.label}
            {selectItem.value === selected?.value && <Icon name="Check" />}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
